import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_PROTESE } from "@/lib/ajuda/dialogos/protese";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth";
import { useOwnedOrganizations } from "@/lib/owned-organizations";
import { useEmOutraOrganizacao } from "@/components/organization/painel/OrganizacaoEmFoco";
import { PatientCombobox } from "@/components/agenda/PatientCombobox";
import { OrganizationOriginBox } from "@/components/shared/OrganizationOriginBox";
import { CreateOrgPicker, type CreateOrgOption } from "@/components/shared/CreateOrgPicker";
import { PatientFormDialog } from "@/components/patients/PatientFormDialog";
import { ProsthesisLabAddDialog } from "@/components/prosthesis/ProsthesisLabAddDialog";

type InitialOrgSnapshot = { id: string; name: string; isPersonal: boolean } | null;

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";

export type OrderStart = {
  patient: { id: string | null; name: string; phone: string };
  labId: string;
  /** Pedido a partir de um item do plano de tratamento: os dentes vêm dele e o pedido fica ligado. */
  doPlano?: { itemId: string; dentes: string | null };
  /** Serviço e material já escolhidos (o "Pedir" de uma linha do catálogo público). */
  servico?: ServicoInicial;
};

export type ServicoInicial = { serviceCategoryId: string; materialOptionId: string | null };

/** Primeiro passo do fluxo de novo pedido — escolhe paciente e laboratório antes de montar os
 *  serviços. Depois de confirmado, esses dois campos ficam travados na tela seguinte. */
export function NewOrderStartDialog({
  open,
  onClose,
  onConfirm,
  pacienteFixo,
  labInicial,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (start: OrderStart) => void;
  /** Abrindo da ficha do paciente não há o que escolher: o paciente é o dono da tela. Passa a
   *  aparecer travado, e o diálogo pergunta só o laboratório. */
  pacienteFixo?: { id: string; name: string; phone?: string | null };
  /** Laboratório já escolhido (veio do catálogo dele): o diálogo pergunta só o paciente. */
  labInicial?: string;
}) {
  const labsQuery = trpc.prosthesis.lab.options.useQuery(undefined, { enabled: open });
  const labs = labsQuery.data ?? [];
  const { user } = useAuth();
  const { clinics } = useOwnedOrganizations();
  // Aberto no painel de outra organização (/organizacoes): o pedido sai dela, e trocar aqui mudaria a
  // organização ativa de verdade.
  const emOutraOrganizacao = useEmOutraOrganizacao();

  const vazio = () =>
    pacienteFixo
      ? { id: pacienteFixo.id as string | null, name: pacienteFixo.name, phone: pacienteFixo.phone ?? "" }
      : { id: null as string | null, name: "", phone: "" };
  const [patient, setPatient] = useState(vazio);
  const [labId, setLabId] = useState(labInicial ?? "");
  const [newPatientDraftName, setNewPatientDraftName] = useState<string | null>(null);
  const [initialOrg, setInitialOrg] = useState<InitialOrgSnapshot>(null);
  const [labAddOpen, setLabAddOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setInitialOrg(
        user?.activeOrganization
          ? { id: user.activeOrganization.id, name: user.activeOrganization.name, isPersonal: user.activeOrganization.isPersonal }
          : null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const orgOptions: CreateOrgOption[] =
    initialOrg?.isPersonal
      ? [
          { id: initialOrg.id, label: `${initialOrg.name} (Pessoal)` },
          ...clinics.filter((c) => c.id !== initialOrg.id).map((c) => ({ id: c.id, label: c.name })),
        ]
      : [];

  function handleOrgSwitched() {
    // Laboratório e paciente selecionados pertencem ao escopo da organização anterior.
    setLabId("");
    setPatient((p) => ({ ...p, id: null }));
  }

  function handleClose() {
    setPatient(vazio());
    setLabId(labInicial ?? "");
    onClose();
  }

  const isValid = patient.name.trim().length >= 2;

  function handleConfirm() {
    if (!isValid) return;
    onConfirm({ patient, labId });
    setPatient(vazio());
    setLabId(labInicial ?? "");
  }

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_PROTESE.novoPedido}>Novo pedido</DialogTitle>
          <DialogDescription>
            {pacienteFixo
              ? "Escolha o laboratório antes de montar os serviços."
              : "Escolha o paciente e o laboratório antes de montar os serviços."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Com paciente travado o seletor de organização sai: trocar de organização invalidaria
              justamente o paciente que motivou a abertura (ele pertence à organização atual). */}
          {orgOptions.length > 1 && !pacienteFixo && !emOutraOrganizacao ? (
            <CreateOrgPicker options={orgOptions} onSwitched={handleOrgSwitched} />
          ) : (
            <OrganizationOriginBox />
          )}

          <div>
            <label className={labelCls}>Paciente *</label>
            {pacienteFixo ? (
              <p className="flex h-9 items-center rounded-md border border-zinc-200 bg-zinc-50 px-3 text-sm font-medium text-zinc-700 dark:border-border dark:bg-muted/40 dark:text-foreground">
                {pacienteFixo.name}
              </p>
            ) : (
              <PatientCombobox
                value={patient}
                onChange={setPatient}
                autoFocus
                onCreateNew={setNewPatientDraftName}
              />
            )}
          </div>

          <div>
            <label className={labelCls}>Laboratório</label>
            <select value={labId} onChange={(e) => setLabId(e.target.value)} className={inputCls}>
              <option value="">Selecione</option>
              {labs.map((l: { id: string; name: string }) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
            {/* Texto e ações em faixas separadas: lado a lado, a explicação do estado vazio
                quebrava em quatro linhas espremidas ao lado dos botões. */}
            <div className="mt-1.5 space-y-1 text-[11px] text-zinc-500 dark:text-muted-foreground">
              <p>
                {!labsQuery.isLoading && labs.length === 0
                  ? "Nenhum laboratório conectado ainda. A lista se atualiza sozinha assim que o laboratório aceitar a conexão."
                  : !labId
                    ? "Pode continuar sem escolher agora, mas vai precisar selecionar um antes de enviar o pedido."
                    : ""}
              </p>
              {/* Atualizar à esquerda, buscar à direita: as duas competiam pelo mesmo canto, e são
                  ações de naturezas diferentes — uma recarrega a lista, a outra abre outro fluxo. */}
              <span className="flex items-center justify-between gap-3">
                {/* Saída manual pra quando o tempo real não deu conta (aba suspensa, SSE caído,
                    laboratório que aceitou horas depois) — sem isso a única opção era F5, que
                    apaga o paciente já escolhido. */}
                <button
                  type="button"
                  onClick={() => labsQuery.refetch()}
                  disabled={labsQuery.isFetching}
                  title="Buscar de novo os laboratórios conectados"
                  className="-my-1.5 inline-flex min-h-[32px] items-center gap-1 rounded-md px-1.5 py-1.5 font-medium text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-60 dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground"
                >
                  <RefreshCw className={cn("h-3 w-3", labsQuery.isFetching && "animate-spin")} />
                  Atualizar
                </button>
                <button
                  type="button"
                  onClick={() => setLabAddOpen(true)}
                  className="-my-1.5 inline-flex min-h-[32px] items-center rounded-md px-1.5 py-1.5 font-medium text-brand-600 hover:underline dark:text-primary"
                >
                  Buscar novo laboratório
                </button>
              </span>
            </div>
          </div>
        </div>

        {/* Proporção: no celular a ação principal ocupa mais largura que Cancelar, pra hierarquia
            não sumir quando os dois estão lado a lado. */}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="h-10 flex-1 sm:h-9 sm:flex-none"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!isValid}
            onClick={handleConfirm}
            className="h-10 flex-[1.4] sm:h-9 sm:flex-none"
          >
            Continuar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <PatientFormDialog
      open={newPatientDraftName !== null}
      initialName={newPatientDraftName ?? undefined}
      onClose={() => setNewPatientDraftName(null)}
      onCreated={(created) => setPatient({ id: created.id, name: created.name, phone: created.phone ?? "" })}
    />

    {/* Fechou a busca: relê a lista. Se a conexão foi imediata (convite já aceito), o laboratório
        já aparece sem o usuário precisar clicar em Atualizar. */}
    <ProsthesisLabAddDialog
      open={labAddOpen}
      onClose={() => {
        setLabAddOpen(false);
        labsQuery.refetch();
      }}
    />
    </>
  );
}
